import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import { useNotifyOnError } from '~/hooks/useNotifyOnError'

import { dialogInfoAtom } from '../atoms'
import { userRoleNameToLabel } from '../constants'
import { useRemoveUserOnBrandAccountMutation } from '../hooks'
import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'

export const DeleteBrandUserDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  if (dialogInfo?.dialogType !== 'delete') {
    throw new Error()
  }

  const { brandAccountUser } = dialogInfo

  const displayEmail = React.useMemo(
    () => brandAccountUser.email,
    [brandAccountUser.email],
  )

  const brandAccountId = useBrandAccountId()
  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const notify = useNotify()
  const notifyError = useNotifyOnError()
  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()

  const translatedRole = brandAccountUser.role
    ? t(userRoleNameToLabel[brandAccountUser.role.name])
    : ''

  const removeUserOnBrandAccountMutation = useRemoveUserOnBrandAccountMutation({
    onSuccess: () => {
      notify({
        content: t('user_deleted', {
          role: translatedRole,
        }),
        style: 'success',
      })

      logEvent('Delete brand account user', {
        eventProperties: [{ key: 'email', value: brandAccountUser.email }],
      })

      closeDialogWithRefresh()
    },
    onError: (data) => {
      notifyError(data, {
        400: t('cannot_delete_the_last_administrator', {
          role: translatedRole,
        }),
        403: t('no_permission_to_delete_users', {
          role: translatedRole,
        }),
      })
      resetDialogInfo()
    },
  })

  const handleExecute = React.useCallback(() => {
    removeUserOnBrandAccountMutation.mutate({
      brandAccountId,
      userId: brandAccountUser.id,
    })
  }, [brandAccountId, brandAccountUser.id, removeUserOnBrandAccountMutation])

  return (
    <ExecuteCancelDialog
      executing={false}
      mode="delete"
      title={t('delete_user', { role: translatedRole })}
      onCancel={resetDialogInfo}
      onExecute={handleExecute}
    >
      {t('delete_confirmation', { target: displayEmail })}
    </ExecuteCancelDialog>
  )
}
